import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useState } from 'react';
import PageContainer from '../../core/components/PageContainer';
import ResponsiveActionButton from '../../core/components/ResponsiveActionButton';
import { usePropertyId } from '../../core/hooks/usePropertyId';
import { baseUrl } from '../../main/client';
import PropertySelectionDialog from '../../property/components/PropertySelectionDialog';

interface LockboxCode {
    id: number;
    name: string;
    code: string;
    from: string;
    to: string;
    created: string;
    updated: string;
}

// API functions
const api = {
    async getLockboxCodes(propertyId: number): Promise<LockboxCode[]> {
        const response = await fetch(
            `${baseUrl}/api/lockbox-codes?propertyId=${propertyId}`,
            {
                headers: {
                    authorization: `bearer ${localStorage.getItem('token')}`,
                },
            }
        );
        if (response.status === 401) {
            localStorage.removeItem('token');
            window.location.reload();
            throw new Error('Unauthorized'); // This won't be reached
        }
        if (!response.ok) {
            throw new Error('Failed to fetch lockbox codes');
        }
        return response.json();
    },

    async createLockboxCode(data: {
        name: string;
        code: string;
        from: string;
        to: string;
        propertyId: number;
    }): Promise<LockboxCode> {
        const response = await fetch(`${baseUrl}/api/lockbox-codes`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                authorization: `bearer ${localStorage.getItem('token')}`,
            },
            body: JSON.stringify(data),
        });
        if (response.status === 401) {
            localStorage.removeItem('token');
            window.location.reload();
            throw new Error('Unauthorized'); // This won't be reached
        }
        if (!response.ok) {
            throw new Error('Failed to create lockbox code');
        }
        return response.json();
    },

    async updateLockboxCode(
        id: number,
        data: {
            name?: string;
            code?: string;
            from?: string;
            to?: string;
        }
    ): Promise<LockboxCode> {
        const response = await fetch(`${baseUrl}/api/lockbox-codes/${id}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                authorization: `bearer ${localStorage.getItem('token')}`,
            },
            body: JSON.stringify(data),
        });
        if (response.status === 401) {
            localStorage.removeItem('token');
            window.location.reload();
            throw new Error('Unauthorized'); // This won't be reached
        }
        if (!response.ok) {
            throw new Error('Failed to update lockbox code');
        }
        return response.json();
    },

    async deleteLockboxCode(id: number): Promise<{ success: boolean }> {
        const response = await fetch(
            `${baseUrl}/api/lockbox-codes/${id}/delete`,
            {
                method: 'POST',
                headers: {
                    authorization: `bearer ${localStorage.getItem('token')}`,
                },
            }
        );
        if (response.status === 401) {
            localStorage.removeItem('token');
            window.location.reload();
            throw new Error('Unauthorized'); // This won't be reached
        }
        if (!response.ok) {
            throw new Error('Failed to delete lockbox code');
        }
        return response.json();
    },
};

interface LockboxCodeFormData {
    name: string;
    code: string;
    from: string;
    to: string;
}

const initialFormData: LockboxCodeFormData = {
    name: '',
    code: '',
    from: '',
    to: '',
};

const LockboxCodeManagement = () => {
    const propertyId = usePropertyId();
    const pathname = window.location.hash.replace('#', '');

    const isLegacyRoute = pathname === '/lockbox';
    const isPropertyRoute = /^\/\d+\/lockbox/.test(pathname);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingCode, setEditingCode] = useState<LockboxCode | null>(null);
    const [formData, setFormData] =
        useState<LockboxCodeFormData>(initialFormData);
    const [lockboxCodes, setLockboxCodes] = useState<LockboxCode[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [formLoading, setFormLoading] = useState(false);

    const loadLockboxCodes = useCallback(async () => {
        if (!propertyId) return;
        try {
            setLoading(true);
            setError(null);
            const codes = await api.getLockboxCodes(propertyId);
            setLockboxCodes(codes);
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : 'Failed to load lockbox codes'
            );
        } finally {
            setLoading(false);
        }
    }, [propertyId]);

    useEffect(() => {
        loadLockboxCodes();
    }, [loadLockboxCodes]);

    if (!propertyId) {
        if (isPropertyRoute) {
            return null;
        }
        if (isLegacyRoute) {
            return (
                <PropertySelectionDialog open={true} targetPath="/lockbox" />
            );
        }
        return null;
    }

    const handleOpenDialog = (code?: LockboxCode) => {
        if (code) {
            setEditingCode(code);
            setFormData({
                name: code.name,
                code: code.code,
                from: DateTime.fromISO(code.from).toFormat(
                    "yyyy-MM-dd'T'HH:mm"
                ),
                to: DateTime.fromISO(code.to).toFormat("yyyy-MM-dd'T'HH:mm"),
            });
        } else {
            setEditingCode(null);
            setFormData(initialFormData);
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingCode(null);
        setFormData(initialFormData);
    };

    const handleSubmit = async () => {
        if (
            !formData.name ||
            !formData.code ||
            !formData.from ||
            !formData.to ||
            !propertyId
        ) {
            return;
        }

        const data = {
            name: formData.name,
            code: formData.code,
            from: new Date(formData.from).toISOString(),
            to: new Date(formData.to).toISOString(),
        };

        try {
            setFormLoading(true);
            if (editingCode) {
                await api.updateLockboxCode(editingCode.id, data);
            } else {
                await api.createLockboxCode({ ...data, propertyId });
            }
            setDialogOpen(false);
            setEditingCode(null);
            setFormData(initialFormData);
            await loadLockboxCodes();
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : 'Failed to save lockbox code'
            );
        } finally {
            setFormLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!editingCode) return;

        try {
            setFormLoading(true);
            await api.deleteLockboxCode(editingCode.id);
            setDialogOpen(false);
            setEditingCode(null);
            setFormData(initialFormData);
            await loadLockboxCodes();
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : 'Failed to delete lockbox code'
            );
        } finally {
            setFormLoading(false);
        }
    };

    const formatDateTime = (isoString: string) => {
        const dt = DateTime.fromISO(isoString);
        return dt.toLocaleString(DateTime.DATETIME_MED);
    };

    return (
        <PageContainer>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                }}
            >
                <Typography
                    variant="h2"
                    component="h1"
                    sx={{
                        fontSize: '2rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                    }}
                >
                    Lockbox Code Management
                </Typography>
                <ResponsiveActionButton
                    label="Add New Code"
                    icon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                />
            </Box>

            {loading ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        py: 8,
                    }}
                >
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                        border: '2px solid #374151',
                        borderRadius: 0,
                    }}
                >
                    <Table aria-label="lockbox codes table">
                        <TableHead>
                            <TableRow
                                sx={{
                                    borderBottom: '3px solid #6366f1',
                                    backgroundColor: '#1a1a2e',
                                }}
                            >
                                <TableCell
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: '0.875rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        py: 2,
                                        px: 2,
                                    }}
                                >
                                    Name
                                </TableCell>
                                <TableCell
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: '0.875rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        px: 2,
                                    }}
                                >
                                    Code
                                </TableCell>
                                <TableCell
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: '0.875rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        px: 2,
                                    }}
                                >
                                    From
                                </TableCell>
                                <TableCell
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: '0.875rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        px: 2,
                                    }}
                                >
                                    To
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {lockboxCodes.length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={4}
                                        align="center"
                                        sx={{ py: 6 }}
                                    >
                                        <Typography
                                            variant="body1"
                                            color="text.secondary"
                                            sx={{ textTransform: 'uppercase' }}
                                        >
                                            No lockbox codes found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                lockboxCodes.map(row => (
                                    <TableRow
                                        key={row.id}
                                        onClick={() => handleOpenDialog(row)}
                                        sx={{
                                            borderBottom: '1px solid #374151',
                                            transition: 'all 0.2s ease',
                                            cursor: 'pointer',
                                            '&:hover': {
                                                backgroundColor:
                                                    'rgba(99, 102, 241, 0.1)',
                                                transform: 'translateX(2px)',
                                            },
                                        }}
                                    >
                                        <TableCell
                                            component="th"
                                            scope="row"
                                            sx={{ py: 2, px: 2 }}
                                        >
                                            <Typography
                                                variant="body2"
                                                fontWeight={600}
                                            >
                                                {row.name}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ px: 2 }}>
                                            <Typography
                                                variant="body1"
                                                fontWeight={700}
                                                sx={{ fontFamily: 'monospace' }}
                                            >
                                                {row.code}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ px: 2 }}>
                                            <Typography variant="body2">
                                                {formatDateTime(row.from)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ px: 2 }}>
                                            <Typography variant="body2">
                                                {formatDateTime(row.to)}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: 0,
                            border: '4px solid #6366f1',
                            backgroundColor: '#1a1a2e',
                        },
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        borderBottom: '3px solid #6366f1',
                        backgroundColor: '#0f0f23',
                        fontWeight: 900,
                        fontSize: '1.5rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        py: 2.5,
                    }}
                >
                    {editingCode ? 'Edit Lockbox Code' : 'Add New Lockbox Code'}
                </DialogTitle>
                <DialogContent
                    sx={{
                        backgroundColor: '#1a1a2e',
                        p: 0,
                    }}
                >
                    <Stack sx={{ py: 3, px: 3 }} spacing={2.5}>
                        {error && (
                            <Alert
                                severity="error"
                                onClose={() => setError(null)}
                                sx={{
                                    borderRadius: 0,
                                    border: '3px solid #ef4444',
                                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                    fontWeight: 600,
                                    '& .MuiAlert-icon': {
                                        color: '#ef4444',
                                    },
                                }}
                            >
                                {error}
                            </Alert>
                        )}
                        <TextField
                            label="Name"
                            value={formData.name}
                            onChange={e =>
                                setFormData({
                                    ...formData,
                                    name: e.target.value,
                                })
                            }
                            fullWidth
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 0,
                                    '& input': {
                                        backgroundColor: '#0f0f23',
                                    },
                                    '& fieldset': {
                                        borderWidth: '2px',
                                        borderColor: '#374151',
                                    },
                                    '&:hover fieldset': {
                                        borderWidth: '2px',
                                        borderColor: '#6366f1',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderWidth: '3px',
                                        borderColor: '#6366f1',
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    fontSize: '1rem',
                                    letterSpacing: '0.05em',
                                },
                            }}
                        />
                        <TextField
                            label="Code"
                            value={formData.code}
                            onChange={e =>
                                setFormData({
                                    ...formData,
                                    code: e.target.value,
                                })
                            }
                            fullWidth
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 0,
                                    '& input': {
                                        backgroundColor: '#0f0f23',
                                        fontFamily: 'monospace',
                                        fontWeight: 700,
                                    },
                                    '& fieldset': {
                                        borderWidth: '2px',
                                        borderColor: '#374151',
                                    },
                                    '&:hover fieldset': {
                                        borderWidth: '2px',
                                        borderColor: '#6366f1',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderWidth: '3px',
                                        borderColor: '#6366f1',
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    fontSize: '1rem',
                                    letterSpacing: '0.05em',
                                },
                            }}
                        />
                        <TextField
                            label="From"
                            type="datetime-local"
                            value={formData.from}
                            onChange={e =>
                                setFormData({
                                    ...formData,
                                    from: e.target.value,
                                })
                            }
                            fullWidth
                            slotProps={{ inputLabel: { shrink: true } }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 0,
                                    '& input': {
                                        backgroundColor: '#0f0f23',
                                    },
                                    '& fieldset': {
                                        borderWidth: '2px',
                                        borderColor: '#374151',
                                    },
                                    '&:hover fieldset': {
                                        borderWidth: '2px',
                                        borderColor: '#6366f1',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderWidth: '3px',
                                        borderColor: '#6366f1',
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    fontSize: '1rem',
                                    letterSpacing: '0.05em',
                                },
                            }}
                        />
                        <TextField
                            label="To"
                            type="datetime-local"
                            value={formData.to}
                            onChange={e =>
                                setFormData({ ...formData, to: e.target.value })
                            }
                            fullWidth
                            slotProps={{ inputLabel: { shrink: true } }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 0,
                                    '& input': {
                                        backgroundColor: '#0f0f23',
                                    },
                                    '& fieldset': {
                                        borderWidth: '2px',
                                        borderColor: '#374151',
                                    },
                                    '&:hover fieldset': {
                                        borderWidth: '2px',
                                        borderColor: '#6366f1',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderWidth: '3px',
                                        borderColor: '#6366f1',
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    fontSize: '1rem',
                                    letterSpacing: '0.05em',
                                },
                            }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions
                    sx={{
                        borderTop: '3px solid #374151',
                        backgroundColor: '#0f0f23',
                        p: 3,
                        gap: 1.5,
                        justifyContent: editingCode
                            ? 'space-between'
                            : 'flex-end',
                    }}
                >
                    {editingCode && (
                        <Button
                            onClick={handleDelete}
                            disabled={formLoading}
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            sx={{
                                borderRadius: 0,
                                borderWidth: '2px',
                                borderColor: 'error.main',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                px: 3,
                                py: 1,
                                '&:hover': {
                                    borderWidth: '2px',
                                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                },
                            }}
                        >
                            Delete
                        </Button>
                    )}
                    <div style={{ display: 'flex', gap: '12px' }}>
                        <Button
                            onClick={handleCloseDialog}
                            disabled={formLoading}
                            variant="outlined"
                            sx={{
                                borderRadius: 0,
                                borderWidth: '2px',
                                borderColor: '#374151',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                px: 3,
                                py: 1,
                                '&:hover': {
                                    borderWidth: '2px',
                                    borderColor: '#374151',
                                    backgroundColor: 'rgba(55, 65, 81, 0.1)',
                                },
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            variant="contained"
                            color="primary"
                            disabled={
                                !formData.name ||
                                !formData.code ||
                                !formData.from ||
                                !formData.to ||
                                formLoading
                            }
                            sx={{
                                borderRadius: 0,
                                border: '2px solid #6366f1',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                px: 3,
                                py: 1,
                            }}
                        >
                            {editingCode ? 'Update' : 'Create'}
                        </Button>
                    </div>
                </DialogActions>
            </Dialog>
        </PageContainer>
    );
};

export default LockboxCodeManagement;
